import {IconButtonWithTooltip} from '@github-ui/icon-button-with-tooltip'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useToastContext} from '@github-ui/toast'
import {CopilotIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup} from '@primer/react'
import type React from 'react'
import type {ReactNode} from 'react'
import {useCallback, useState} from 'react'

import {publishAddCopilotChatReference, publishOpenCopilotChat} from '../utils/copilot-chat-events'
import type {CopilotChatReference} from '../utils/copilot-chat-types'

// For some reason the tooltip makes these icons 16x18px which makes them off-center in the button
// Explicitly set the height to 16px to fix this
const squareHeightSx = {
  height: '24px',
  'span[role=tooltip]': {
    height: '16px',
  },
}

export default function CopilotChatButton({
  messageReference,
  style,
}: {
  messageReference: CopilotChatReference
  style?: React.CSSProperties
}) {
  const [open, setOpen] = useState(false)
  const copilotChatEnabled = useFeatureFlag('copilot_conversational_ux')
  const suggestIcebreakerEnabled = useFeatureFlag('copilot_smell_icebreaker_ux')
  const {addToast} = useToastContext()

  const handleExplain = useCallback(() => {
    publishOpenCopilotChat({
      content: 'Explain',
      intent: 'explain',
      references: [messageReference],
    })
    setOpen(false)
  }, [messageReference])

  const handleSuggest = useCallback(() => {
    publishOpenCopilotChat({
      content: 'Suggest improvements to this code.',
      intent: 'suggest',
      references: [messageReference],
    })
    setOpen(false)
  }, [messageReference])

  const handleAskAbout = useCallback(() => {
    publishOpenCopilotChat({
      intent: 'conversation',
      references: [messageReference],
    })
    setOpen(false)
  }, [messageReference])

  const handleAddReferenceCallback = useCallback(() => {
    handleAddReference(messageReference, false, addToast)
    setOpen(false)
  }, [addToast, messageReference])

  return copilotChatEnabled ? (
    <ButtonGroup sx={style || {pr: 3}}>
      <IconButtonWithTooltip
        icon={CopilotIcon}
        size="small"
        label={`Ask Copilot about this ${messageReference.type}`}
        onClick={handleAskAbout}
        sx={squareHeightSx}
        tooltipDirection="sw"
        data-testid="copilot-ask-menu"
      />
      <ActionMenu open={open} onOpenChange={setOpen}>
        <ActionMenu.Anchor>
          <IconButtonWithTooltip
            icon={TriangleDownIcon}
            label="Copilot menu"
            onSelect={() => setOpen(true)}
            size="small"
            sx={squareHeightSx}
            tooltipDirection="sw"
            data-testid="more-copilot-button"
          />
        </ActionMenu.Anchor>

        <ActionMenu.Overlay align="end">
          <ActionList>
            <ActionList.Item onSelect={handleExplain}>Explain</ActionList.Item>
            {suggestIcebreakerEnabled ? (
              <ActionList.Item onSelect={handleSuggest}>Suggest Improvements</ActionList.Item>
            ) : null}
            <ActionList.Divider />
            <ActionList.Item onSelect={handleAddReferenceCallback}>Attach to current thread</ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </ButtonGroup>
  ) : null
}

/** `addToast` prop is required if `shouldOpenPanel` is false */
export const handleAddReference = (
  messageReference: CopilotChatReference,
  shouldOpenPanel: boolean,
  addToast?: (toast: {message: ReactNode; type: 'info' | 'success' | 'error'}) => void,
) => {
  if (shouldOpenPanel) {
    publishOpenCopilotChat({intent: 'conversation', references: [messageReference]})
  } else if (addToast) {
    publishAddCopilotChatReference(messageReference)
    addToast({message: 'Reference added to thread', type: 'success'})
  }
}

try{ CopilotChatButton.displayName ||= 'CopilotChatButton' } catch {}